/** Compact query-syntax reference, shown from the help icon next to the input. */
import { Box, Popover, Typography } from '@mui/material';

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

interface Row {
  label: string;
  example: string;
  note: string;
}

const ROWS: Row[] = [
  {
    label: 'Condition',
    example: 'tenant=curatedplanet',
    note: 'exact match — add "~" for contains, e.g. tag=~mariott',
  },
  { label: 'AND', example: 'a=1 b=2', note: 'adjacent conditions are ANDed ("&" also works)' },
  { label: 'OR', example: 'a=1 | b=2', note: 'either condition matches' },
  { label: 'NOT', example: '!a=1', note: 'negates the next condition or group' },
  { label: 'Grouping', example: '(a=1 | b=2) & c=3', note: 'parentheses control precedence' },
  {
    label: 'Ranges',
    example: 'created=2026-07-01..2026-07-08',
    note: 'from..to; namespaces that support it also take open ends: v.. (greater than), ..v (less than)',
  },
  {
    label: 'Live time',
    example: 'created=now-15m..now',
    note: 'relative to now, re-read on every refresh: m h d w M (minutes … months); now+2d looks ahead',
  },
  { label: 'Free text', example: 'ORDER-REF-123', note: 'bare words search common reference fields' },
  { label: 'Quoting', example: 'name="John Smith"', note: 'quote values that contain spaces' },
  {
    label: 'Paste',
    example: 'tenant=cp & (tag=a | tag=b)',
    note: 'a pasted expression is added after what is already there, condition by condition',
  },
];

export function SyntaxHelpPopover({ anchorEl, onClose }: Props) {
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ p: 2, maxWidth: 380 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Query syntax
        </Typography>
        {ROWS.map((row) => (
          <Box key={row.label} sx={{ mb: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 68, flexShrink: 0 }}>
                {row.label}
              </Typography>
              <Typography
                variant="caption"
                component="code"
                sx={{ fontFamily: 'monospace', color: 'text.primary', wordBreak: 'break-all' }}
              >
                {row.example}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: '76px' }}>
              {row.note}
            </Typography>
          </Box>
        ))}
      </Box>
    </Popover>
  );
}
